import React, { useState } from 'react';
import { X, Mail, Lock, Shield, Loader2, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import QRCode from 'qrcode';

type Step = 'password' | 'new-email' | 'qr-code' | 'verify-code' | 'success';

interface EmailChangeModalProps {
  currentEmail: string;
  onClose: () => void;
  onSuccess: (newEmail: string) => void;
  theme: string;
}

export default function EmailChangeModal({ currentEmail, onClose, onSuccess, theme }: EmailChangeModalProps) {
  const [step, setStep] = useState<Step>('password');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [authCode, setAuthCode] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [manualKey, setManualKey] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const isDark = theme === 'dark';

  const stepTitles: Record<Step, string> = {
    'password': 'Verify Your Password',
    'new-email': 'Enter New Email',
    'qr-code': 'Scan New QR Code',
    'verify-code': 'Enter Auth Code',
    'success': 'Email Updated',
  };

  const stepNumbers: Record<Step, number> = {
    'password': 1,
    'new-email': 2,
    'qr-code': 3,
    'verify-code': 4,
    'success': 4,
  };

  const handlePasswordStep = async () => {
    if (!password) {
      setError('Please enter your current password.');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: currentEmail,
        password,
      });
      if (signInError) throw new Error('Incorrect password. Please try again.');
      setStep('new-email');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Password verification failed.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewEmailStep = async () => {
    if (!newEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (newEmail.toLowerCase() === currentEmail.toLowerCase()) {
      setError('New email must be different from your current email.');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const session = await supabase.auth.getSession();
      const accessToken = session.data.session?.access_token;
      if (!accessToken) throw new Error('Session expired. Please log in again.');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/twofa-setup`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            'Apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ pending_email: newEmail }),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to generate QR code.');
      }

      const data = await response.json();
      const qrDataUrl = await QRCode.toDataURL(data.otpauth_url);
      setQrCodeUrl(qrDataUrl);
      setManualKey(data.manual_entry_key);
      setStep('qr-code');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate new authenticator setup.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyStep = async () => {
    if (!authCode || authCode.length !== 6 || !/^\d{6}$/.test(authCode)) {
      setError('Please enter the 6-digit code from Google Authenticator.');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const session = await supabase.auth.getSession();
      const accessToken = session.data.session?.access_token;
      if (!accessToken) throw new Error('Session expired. Please log in again.');

      const verifyResponse = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/twofa-verify`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            'Apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ token: authCode, enable: true }),
        }
      );

      if (!verifyResponse.ok) {
        const data = await verifyResponse.json();
        throw new Error(data.error || 'Invalid authentication code.');
      }

      const changeResponse = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/change-email-with-2fa`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            'Apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ new_email: newEmail, totp_token: authCode }),
        }
      );

      if (!changeResponse.ok) {
        const data = await changeResponse.json();
        throw new Error(data.error || 'Failed to update email.');
      }

      setStep('success');
      setTimeout(() => {
        onSuccess(newEmail);
      }, 1800);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed.');
    } finally {
      setIsLoading(false);
    }
  };

  const inputClass = `w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent outline-none transition-colors ${
    isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
  }`;

  const overlayClass = 'fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm';

  const modalClass = `relative w-full max-w-md rounded-2xl shadow-2xl ${
    isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-100'
  }`;

  return (
    <div className={overlayClass}>
      <div className={modalClass}>
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
          <div>
            <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Change Email Address
            </h2>
            <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {stepTitles[step]}
            </p>
          </div>
          {step !== 'success' && (
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Step Indicators */}
        {step !== 'success' && (
          <div className="flex items-center gap-1.5 px-6 pt-4">
            {[1, 2, 3, 4].map((n) => (
              <div
                key={n}
                className={`h-1.5 flex-1 rounded-full transition-colors ${
                  n <= stepNumbers[step] ? 'bg-[#40E0D0]' : isDark ? 'bg-gray-700' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
        )}

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Password */}
          {step === 'password' && (
            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${isDark ? 'bg-blue-900/20 border border-blue-800/40' : 'bg-blue-50 border border-blue-200'}`}>
                <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                  For your security, please confirm your current password before changing your email.
                </p>
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Current Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(''); }}
                    onKeyDown={(e) => e.key === 'Enter' && handlePasswordStep()}
                    placeholder="Enter your password"
                    className={`${inputClass} pl-10 pr-10`}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {error && <p className="text-red-500 text-sm">{error}</p>}
              <button
                onClick={handlePasswordStep}
                disabled={isLoading}
                className="w-full py-3 bg-[#40E0D0] hover:bg-[#40E0D0]/90 text-black font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {isLoading ? 'Verifying...' : 'Continue'}
              </button>
            </div>
          )}

          {/* Step 2: New Email */}
          {step === 'new-email' && (
            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700/50 border border-gray-600' : 'bg-gray-50 border border-gray-200'}`}>
                <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  Current email: <span className="font-medium">{currentEmail}</span>
                </p>
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  New Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => { setNewEmail(e.target.value); setError(''); }}
                    onKeyDown={(e) => e.key === 'Enter' && handleNewEmailStep()}
                    placeholder="you@example.com"
                    className={`${inputClass} pl-10`}
                    autoFocus
                  />
                </div>
              </div>
              {error && <p className="text-red-500 text-sm">{error}</p>}
              <div className="flex gap-3">
                <button
                  onClick={() => setStep('password')}
                  className={`flex-1 py-3 rounded-lg font-semibold transition-colors ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                >
                  Back
                </button>
                <button
                  onClick={handleNewEmailStep}
                  disabled={isLoading}
                  className="flex-1 py-3 bg-[#40E0D0] hover:bg-[#40E0D0]/90 text-black font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {isLoading ? 'Generating...' : 'Continue'}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: QR Code */}
          {step === 'qr-code' && (
            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${isDark ? 'bg-amber-900/20 border border-amber-700/40' : 'bg-amber-50 border border-amber-200'}`}>
                <p className={`text-sm ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                  <strong>Important:</strong> Your new QR code is tied to <strong>{newEmail}</strong>. Scan it now to re-link your Google Authenticator app.
                </p>
              </div>
              {qrCodeUrl && (
                <div className="flex flex-col items-center gap-3">
                  <div className="p-3 bg-white rounded-xl border-2 border-[#40E0D0]/30">
                    <img src={qrCodeUrl} alt="QR Code" className="w-44 h-44" />
                  </div>
                  {manualKey && (
                    <div className={`w-full p-3 rounded-lg text-center ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <p className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Manual entry key:</p>
                      <p className={`text-sm font-mono font-semibold tracking-wider break-all ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {manualKey}
                      </p>
                    </div>
                  )}
                </div>
              )}
              {error && <p className="text-red-500 text-sm">{error}</p>}
              <div className="flex gap-3">
                <button
                  onClick={() => setStep('new-email')}
                  className={`flex-1 py-3 rounded-lg font-semibold transition-colors ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                >
                  Back
                </button>
                <button
                  onClick={() => setStep('verify-code')}
                  className="flex-1 py-3 bg-[#40E0D0] hover:bg-[#40E0D0]/90 text-black font-semibold rounded-lg transition-colors"
                >
                  I've Scanned It
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Verify TOTP Code */}
          {step === 'verify-code' && (
            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700/50 border border-gray-600' : 'bg-gray-50 border border-gray-200'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Shield className="w-4 h-4 text-[#40E0D0]" />
                  <p className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                    Google Authenticator Verification
                  </p>
                </div>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Open Google Authenticator and enter the 6-digit code for the new entry you just scanned.
                </p>
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  6-Digit Code
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={authCode}
                  onChange={(e) => { setAuthCode(e.target.value.replace(/\D/g, '')); setError(''); }}
                  onKeyDown={(e) => e.key === 'Enter' && handleVerifyStep()}
                  placeholder="000000"
                  className={`${inputClass} text-center text-2xl tracking-[0.5em] font-mono`}
                  autoFocus
                />
              </div>
              {error && <p className="text-red-500 text-sm">{error}</p>}
              <div className="flex gap-3">
                <button
                  onClick={() => setStep('qr-code')}
                  className={`flex-1 py-3 rounded-lg font-semibold transition-colors ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                >
                  Back
                </button>
                <button
                  onClick={handleVerifyStep}
                  disabled={isLoading}
                  className="flex-1 py-3 bg-[#40E0D0] hover:bg-[#40E0D0]/90 text-black font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {isLoading ? 'Confirming...' : 'Confirm & Change'}
                </button>
              </div>
            </div>
          )}

          {/* Success */}
          {step === 'success' && (
            <div className="flex flex-col items-center gap-4 py-4 text-center">
              <div className="w-16 h-16 rounded-full bg-[#40E0D0]/10 flex items-center justify-center">
                <CheckCircle className="w-9 h-9 text-[#40E0D0]" />
              </div>
              <div>
                <h3 className={`text-lg font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Email Changed Successfully
                </h3>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Your email has been updated to <span className="font-medium text-[#40E0D0]">{newEmail}</span>.<br />
                  Your Google Authenticator has been re-linked.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
